/** Deep-link helpers for group main feed and thread URLs (#post-…). */

import {
  buildGroupFeedHashUrl,
  syncFeedToPostStream,
  waitForElement
} from './group-permalink.js';

export function parseGroupLocationHash(hash = window.location.hash) {
  if (!hash || hash.length < 2) {
    return null;
  }
  if (hash.startsWith('#post-')) {
    const postId = parseInt(hash.slice('#post-'.length), 10);
    return Number.isNaN(postId) ? null : { type: 'post', postId };
  }
  return null;
}

export function isGroupFeedPath(pathname = window.location.pathname) {
  return /^\/groups\/\d+(?:\/threads\/\d+)?$/.test(pathname);
}

export function parseGroupFeedPath(pathname = window.location.pathname) {
  const threadMatch = pathname.match(/^\/groups\/(\d+)\/threads\/(\d+)$/);
  if (threadMatch) {
    return { groupId: parseInt(threadMatch[1], 10), threadId: parseInt(threadMatch[2], 10) };
  }
  const groupMatch = pathname.match(/^\/groups\/(\d+)$/);
  if (groupMatch) {
    return { groupId: parseInt(groupMatch[1], 10), threadId: null };
  }
  return null;
}

function focusPostElement(anchor) {
  const postItem = anchor.closest('post-item');
  if (postItem && typeof postItem.openComments === 'function') {
    postItem.openComments(true);
  }
  anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/**
 * Expand comments and scroll to a group post when the URL hash is #post-{id}.
 * @param {{ root?: ParentNode, feed?: HTMLElement, allowResolve?: boolean }} [options]
 * @returns {Promise<boolean>}
 */
export async function focusGroupFeedHash({ root = document, feed = null, allowResolve = true } = {}) {
  if (!isGroupFeedPath()) {
    return false;
  }

  const parsed = parseGroupLocationHash();
  if (!parsed || parsed.type !== 'post') {
    return false;
  }

  const pathContext = parseGroupFeedPath();
  const isRetry = Boolean(feed?._hashFocusRetry);
  if (feed) {
    feed._hashFocusRetry = false;
  }

  let anchor = root.querySelector?.(`#post-${parsed.postId}`) || document.getElementById(`post-${parsed.postId}`);

  if (!anchor && allowResolve && !isRetry && pathContext) {
    const navigated = await syncFeedToPostStream({
      postId: parsed.postId,
      feed,
      pathContext
    });
    if (navigated) {
      return navigated;
    }
  }

  anchor = await waitForElement(`#post-${parsed.postId}`, root);
  if (!anchor) {
    if (allowResolve && !isRetry && pathContext) {
      return syncFeedToPostStream({
        postId: parsed.postId,
        feed,
        pathContext
      });
    }
    return false;
  }

  focusPostElement(anchor);
  return true;
}

// Re-export for callers that build hash navigation URLs
export { buildGroupFeedHashUrl };
